import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, TrendingUp, AlertTriangle, Plus, Edit2, Save, X, Percent } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { supabase } from '../../lib/supabase';
import { realtimeService } from '../../services/realtimeService';

export default function EmptyBarrelsSalesManagement() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [sales, setSales] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingInventory, setEditingInventory] = useState(null);
  const [showNewSaleModal, setShowNewSaleModal] = useState(false);
  const [newSale, setNewSale] = useState({
    clientName: '',
    clientPhone: '',
    barrelType: 'noir_270l',
    quantity: 1,
    unitPrice: 35,
    paymentStatus: 'pending'
  });

  useEffect(() => {
    loadData();

    // Subscribe to barrel_sales real-time updates
    const salesSubscription = realtimeService?.subscribeToBarrelSales((payload) => {
      console.log('Barrel sales real-time update:', payload);
      // Reload data when barrel_sales changes (INSERT, UPDATE, DELETE)
      loadData();
    });

    // Subscribe to barrel_deposits real-time updates
    const depositsSubscription = realtimeService?.subscribeToBarrelDeposits((payload) => {
      console.log('Barrel deposits real-time update:', payload);
      // Reload data when barrel_deposits changes (driver validates payment)
      loadData();
    });

    // Cleanup subscriptions on unmount
    return () => {
      realtimeService?.unsubscribe('barrel-sales-realtime');
      realtimeService?.unsubscribe('barrel-deposits-realtime');
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [inventoryData, salesData, depositsData] = await Promise.all([
        barrelService?.getInventory(),
        barrelService?.getBarrelOrders(),
        barrelService?.getDeposits()
      ]);
      setInventory(inventoryData);
      setSales(salesData);
      setDeposits(depositsData);
    } catch (error) {
      console.error('Error loading barrel data:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInventory = async (id, updates) => {
    try {
      // Map camelCase to snake_case for database
      const dbUpdates = {
        barrel_type: updates?.barrelType,
        price_euros: updates?.priceEuros,
        stock_quantity: updates?.stockQuantity,
        reorder_threshold: updates?.reorderThreshold
      };
      
      await supabase
        ?.from('barrel_inventory')
        ?.update(dbUpdates)
        ?.eq('id', id);
        
      await loadData();
      setEditingInventory(null);
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleCreateSale = async () => {
    try {
      const totalPrice = newSale?.quantity * newSale?.unitPrice;
      await barrelService?.createSale({
        ...newSale,
        totalPrice,
        orderStatus: 'pending'
      });
      await loadData();
      setShowNewSaleModal(false);
      setNewSale({
        clientName: '',
        clientPhone: '',
        barrelType: 'noir_270l',
        quantity: 1,
        unitPrice: 35,
        paymentStatus: 'pending'
      });
    } catch (error) {
      console.error('Error creating sale:', error);
      alert('Erreur lors de la création de la vente');
    }
  };

  const handleUpdateSaleStatus = async (id, status) => {
    try {
      await barrelService?.updateSale(id, { orderStatus: status });
      await loadData();
    } catch (error) {
      console.error('Error updating sale status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getBarrelLabel = (type) => {
    return type === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      delivered: 'Livré',
      cancelled: 'Annulé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges?.[status] || badges?.pending}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const badges = {
      pending: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      pending: 'Non payé',
      paid: 'Payé',
      partial: 'Partiel'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges?.[status] || badges?.pending}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const totalRevenue = sales
    ?.filter(s => s?.paymentStatus === 'paid')
    ?.reduce((sum, s) => sum + (parseFloat(s?.totalPrice) || 0), 0);

  const pendingOrders = sales?.filter(s => s?.orderStatus === 'pending')?.length || 0;

  const totalMargin = sales
    ?.filter(s => s?.paymentStatus === 'paid')
    ?.reduce((sum, s) => sum + (parseFloat(s?.totalPrice) || 0) - (parseFloat(s?.unitPrice) || 0) * (parseFloat(s?.quantity) || 0), 0);

  const averageMarginPercent = totalMargin && sales?.filter(s => s?.paymentStatus === 'paid')?.length > 0
    ? ((totalMargin / parseFloat(sales?.filter(s => s?.paymentStatus === 'paid')?.reduce((sum, s) => sum + (parseFloat(s?.unitPrice) || 0) * (parseFloat(s?.quantity) || 0), 0))) * 100)?.toFixed(2)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-8 h-8 text-green-600" />
            Gestion Vente Barriques Vides
          </h1>
          <p className="mt-2 text-gray-600">
            Gestion de l'inventaire, des ventes et du suivi des barriques vides
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Stock Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inventory?.reduce((sum, item) => sum + (item?.stockQuantity || 0), 0)}
                </p>
              </div>
              <Package className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Commandes en attente</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingOrders}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenu Total</p>
                <p className="text-2xl font-bold text-green-600">{totalRevenue?.toFixed(2)}€</p>
                <p className="text-xs text-gray-500 mt-1">Ventes payées uniquement</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Marge Totale</p>
                <p className="text-2xl font-bold text-blue-600">{totalMargin?.toFixed(2)}€</p>
                <p className="text-xs text-gray-500 mt-1">Marge moyenne: {averageMarginPercent}%</p>
              </div>
              <Percent className="w-10 h-10 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Inventory Management */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-6 h-6 text-green-600" />
              Inventaire des Barriques
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Gestion des stocks et tarification
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Unitaire</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seuil Réappro</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventory?.map((item) => (
                  <tr key={item?.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {getBarrelLabel(item?.barrelType)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingInventory?.id === item?.id ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editingInventory?.priceEuros}
                          onChange={(e) => setEditingInventory({ ...editingInventory, priceEuros: e?.target?.value })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{item?.priceEuros}€</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingInventory?.id === item?.id ? (
                        <input
                          type="number"
                          value={editingInventory?.stockQuantity}
                          onChange={(e) => setEditingInventory({ ...editingInventory, stockQuantity: parseInt(e?.target?.value) })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded"
                        />
                      ) : (
                        <span className={`text-sm font-semibold ${
                          item?.stockQuantity <= item?.reorderThreshold ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {item?.stockQuantity}
                          {item?.stockQuantity <= item?.reorderThreshold && (
                            <AlertTriangle className="w-4 h-4 inline ml-1" />
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingInventory?.id === item?.id ? (
                        <input
                          type="number"
                          value={editingInventory?.reorderThreshold}
                          onChange={(e) => setEditingInventory({ ...editingInventory, reorderThreshold: parseInt(e?.target?.value) })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{item?.reorderThreshold}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingInventory?.id === item?.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateInventory(item?.id, editingInventory)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Save className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setEditingInventory(null)}
                            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingInventory(item)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sales Management */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                Commandes de Barriques
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Suivi des ventes et commandes clients
              </p>
            </div>
            <button
              onClick={() => setShowNewSaleModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Plus className="w-5 h-5" />
              Nouvelle Vente
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paiement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sales?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Aucune vente enregistrée</p>
                    </td>
                  </tr>
                ) : (
                  sales?.map((sale) => (
                    <tr key={sale?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(sale?.orderDate)?.toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{sale?.clientName}</div>
                        <div className="text-sm text-gray-500">{sale?.clientPhone}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {getBarrelLabel(sale?.barrelType)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{sale?.quantity}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {parseFloat(sale?.totalPrice)?.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4">{getPaymentBadge(sale?.paymentStatus)}</td>
                      <td className="px-6 py-4">{getStatusBadge(sale?.orderStatus)}</td>
                      <td className="px-6 py-4">
                        {sale?.orderStatus === 'pending' && (
                          <button
                            onClick={() => handleUpdateSaleStatus(sale?.id, 'confirmed')}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Confirmer
                          </button>
                        )}
                        {sale?.orderStatus === 'confirmed' && (
                          <button
                            onClick={() => handleUpdateSaleStatus(sale?.id, 'delivered')}
                            className="text-sm text-green-600 hover:text-green-800 font-medium"
                          >
                            Marquer livré
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* New Sale Modal */}
      {showNewSaleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Nouvelle Vente de Barrique</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client</label>
                <input
                  type="text"
                  value={newSale?.clientName}
                  onChange={(e) => setNewSale({ ...newSale, clientName: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Nom complet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newSale?.clientPhone}
                  onChange={(e) => setNewSale({ ...newSale, clientPhone: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="+33..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de barrique</label>
                <select
                  value={newSale?.barrelType}
                  onChange={(e) => {
                    const type = e?.target?.value;
                    const price = type === 'noir_270l' ? 35 : 40;
                    setNewSale({ ...newSale, barrelType: type, unitPrice: price });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="noir_270l">Barrique Noire 270L (35€)</option>
                  <option value="bleu_220l">Barrique Bleue 220L (40€)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={newSale?.quantity}
                  onChange={(e) => setNewSale({ ...newSale, quantity: parseInt(e?.target?.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut paiement</label>
                <select
                  value={newSale?.paymentStatus}
                  onChange={(e) => setNewSale({ ...newSale, paymentStatus: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="pending">Non payé</option>
                  <option value="paid">Payé</option>
                  <option value="partial">Partiel</option>
                </select>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Prix total:</p>
                <p className="text-2xl font-bold text-green-600">
                  {(newSale?.quantity * newSale?.unitPrice)?.toFixed(2)}€
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleCreateSale}
                disabled={!newSale?.clientName || !newSale?.clientPhone}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Créer la vente
              </button>
              <button
                onClick={() => setShowNewSaleModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}